import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { WorkProgressMaterialItem, WorkProgressMaterialsResponse, BatchInfo } from '../interfaces/work-progress-material.interface';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ExportDialogComponent, ExportFileFormat, ExportRange } from 'app/layout/export-overlay';
import { WorkProgressMaterialExport } from './work-progress-material-export.service';
import { SimpleBatchesViewerComponent, SimpleBatchesDialogResponse } from 'app/modules/shared/simple-batches-viewer/simple-batches-viewer.component';
import { SimpleBatchesDataService } from 'app/modules/shared/simple-batches-viewer/simple-batches-data.service';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { UnitMeasureDisplayComponent } from "app/shared/components/unit-measure-display/unit-measure-display.component";

// Smart Grid Imports
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';

// Cell Renderer Imports
import { MaterialCellRendererComponent } from './cell-renderers/material-cell-renderer.component';
import { ClientCellRendererComponent } from './cell-renderers/client-cell-renderer.component';
import { DocumentNavigationWrapperCellRendererComponent } from './cell-renderers/document-navigation-wrapper-cell-renderer.component';
import { MaterialActionsCellRendererComponent } from './cell-renderers/material-actions-cell-renderer.component';
import { ExportHeaderButtonComponent } from './cell-renderers/export-header-button.component';
import { WorkProgressQuantityCellRendererComponent } from './cell-renderers/work-progress-quantity-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

// Interface para las opciones de exportación
export interface ExportOptions {
  format: ExportFileFormat;
  range: ExportRange;
}

// Constante para el título del modal
const WORK_PROGRESS_MATERIAL_TITLE = "Exportar Materiales - Trabajo en Curso";

@Component({
  selector: "app-work-progress-material-summary",
  standalone: true,
  imports: [
    SmartGridComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    DocumentNavigationComponent,
    UnitMeasureDisplayComponent
  ],
  templateUrl: "./work-progress-material-summary.component.html",
  styleUrl: "./work-progress-material-summary.component.scss",
  providers: [WorkProgressMaterialExport],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkProgressMaterialSummaryComponent implements OnInit, OnDestroy {
  private _materialData: WorkProgressMaterialsResponse | null = null;

  @Input()
  set materialData(value: WorkProgressMaterialsResponse | null) {
    this._materialData = value;
  }

  get materialData(): WorkProgressMaterialsResponse | null {
    return this._materialData;
  }

  @Input() isLoading = false;
  @Output() initComponent = new EventEmitter<void>();

  // habilitar/ocultar UMP si aplica en tu entorno (Observable for reactive updates)
  @Input() UMPActive$: Observable<boolean> = new BehaviorSubject<boolean>(true);

  // Current UMP value for synchronous access
  private currentUMPActive: boolean = true;
  private umpSubscription?: Subscription;

  // Smart Grid Column Definitions
  materialSummaryColumnDefs: ColDef<WorkProgressMaterialItem>[] = [];
  batchesColumnDefs: ColDef<BatchInfo>[] = [];

  // Smart Grid Configurations
  materialSummaryGridConfig: GridConfig<WorkProgressMaterialItem> = {};
  batchesGridConfig: GridConfig<BatchInfo> = {};

  constructor(
    private dialog: MatDialog,
    private workProgressMaterialExport: WorkProgressMaterialExport,
    private simpleBatchesDataService: SimpleBatchesDataService
  ) { }

  ngOnInit(): void {
    // Subscribe to UMPActive$ to track current value
    this.umpSubscription = this.UMPActive$.subscribe(value => {
      this.currentUMPActive = value;
    });

    this.initializeMaterialSummaryColumnDefs();
    this.initializeBatchesColumnDefs();
    this.initializeMaterialSummaryGridConfig();
    this.initializeBatchesGridConfig();

    // Emit event to parent component to load data if not already loaded
    this.initComponent.emit();
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.umpSubscription?.unsubscribe();
  }

  private initializeMaterialSummaryColumnDefs(): void {
    this.materialSummaryColumnDefs = [
      // 1. MATERIAL COLUMN
      {
        colId: 'material',
        field: 'materialName' as any,
        headerName: 'Material',
        width: 250,
        minWidth: 200,
        maxWidth: 300,
        sortable: true,
        filterable: true,
        pinned: 'left',
        cellRenderer: MaterialCellRendererComponent,
        valueGetter: (data) => data.materialName,
        responsive: {
          hideOnMobile: false,
          responsivePriority: 1
        }
      },

      // 2. CLIENT COLUMN
      {
        colId: 'client',
        field: 'clientName' as any,
        headerName: 'Cliente',
        width: 200,
        minWidth: 180,
        maxWidth: 250,
        sortable: true,
        filterable: true,
        cellRenderer: ClientCellRendererComponent,
        valueGetter: (data) => data.clientName,
        responsive: {
          hideOnMobile: false,
          responsivePriority: 2
        }
      },

      // 3. DOCUMENT NUMBER COLUMN
      {
        colId: 'documentNumber',
        field: 'documentNumber' as any,
        headerName: 'Nro documento',
        width: 180,
        minWidth: 150,
        sortable: true,
        filterable: true,
        cellRenderer: DocumentNavigationWrapperCellRendererComponent,
        valueGetter: (data) => data.documentNumber,
        responsive: {
          hideOnMobile: false,
          responsivePriority: 3
        }
      },

      // 4. DOCUMENT CLASS COLUMN
      {
        field: 'documentClass' as any,
        headerName: 'Clase documento',
        width: 180,
        minWidth: 150,
        sortable: true,
        filterable: true,
        responsive: {
          hideOnTablet: true,
          responsivePriority: 5
        }
      },

      // 5. QUANTITY COLUMN (UMB + UMP)
      {
        colId: 'qty',
        headerName: 'Cantidad',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: WorkProgressQuantityCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
        },
        responsive: {
          hideOnMobile: false,
          responsivePriority: 4
        }
      },

      // 6. TOTAL COLUMN
      {
        field: 'total' as any,
        headerName: 'Total',
        width: 150,
        minWidth: 120,
        sortable: true,
        filterable: true,
        cellRenderer: DecimalCellRendererComponent,
        cellClass: 'text-right',
        headerClass: 'text-right',
        responsive: {
          hideOnTablet: true,
          responsivePriority: 6
        }
      },

      // 7. ACTIONS COLUMN
      {
        colId: 'actions',
        headerName: '',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        sortable: false,
        filterable: false,
        pinned: 'right',
        lockVisible: true,
        cellRenderer: MaterialActionsCellRendererComponent,
        cellRendererParams: {
          onVerLotes: (data: WorkProgressMaterialItem) => this.openBatchesDetail(data),
          onExportarMaterial: (data: WorkProgressMaterialItem) => this.exportSingleMaterial(data),
          onExportarLotes: (data: WorkProgressMaterialItem) => this.exportMaterialBatches(data)
        },
        cellClass: 'text-center',
        headerRenderer: ExportHeaderButtonComponent,
        headerRendererParams: {
          params: {
            onExport: () => this.onExportButtonPressed(),
            tooltip: 'Exportar todos los materiales'
          }
        },
        responsive: {
          hideOnMobile: false,
          responsivePriority: 7
        }
      }
    ];
  }

  private initializeBatchesColumnDefs(): void {
    this.batchesColumnDefs = [
      {
        field: 'batchNumber' as any,
        headerName: 'Lote',
        width: 150,
        cellClass: 'font-bold text-xs'
      },
      {
        field: 'quantity' as any,
        headerName: 'Cantidad',
        width: 120,
        cellRenderer: DecimalCellRendererComponent
      },
      {
        field: 'expirationDate' as any,
        headerName: 'Vencimiento',
        width: 140,
        valueFormatter: (value) => value || '-'
      },
      {
        field: 'manufacturingDate' as any,
        headerName: 'Fabricación',
        width: 140,
        valueFormatter: (value) => value || '-'
      },
      {
        field: 'status' as any,
        headerName: 'Estado',
        width: 120,
        valueFormatter: (value) => value || '-'
      },
      {
        colId: 'warehouse',
        headerName: 'Almacén',
        width: 200,
        valueGetter: (data: any) => {
          if (!data.warehouse) return '-';
          return `${data.warehouse.code || ''} ${data.warehouse.name ? '· ' + data.warehouse.name : ''}`;
        }
      }
    ];
  }

  private initializeMaterialSummaryGridConfig(): void {
    this.materialSummaryGridConfig = {
      // DATA SOURCE
      dataMode: 'local',
      rowIdField: '_id' as any,

      // SORTING
      sorting: {
        enabled: true,
        mode: 'local',
        multiColumn: false,
      },

      // PAGINATION
      pagination: {
        enabled: true,
        mode: 'local',
        pageSize: 50,
        pageSizeOptions: [10, 50, 100, 150],
        showPaginator: true,
        showFirstLastButtons: true,
        position: 'bottom'
      },

      // SEARCH
      search: {
        enabled: false,
        mode: 'local',
        showSearchInput: false
      },

      // COLUMN FILTERS
      columnFilters: {
        enabled: true,
        mode: 'local',
        showFilterIcon: true,
        trigger: 'click'
      },

      // SELECTION
      selection: {
        enabled: false
      },

      // SETTINGS PANEL
      settingsPanel: {
        enabled: true,
        position: 'right',
        enableColumnVisibility: true,
        enableDensity: false,
        enableExport: false,
        defaultDensity: 'comfortable'
      },

      // COLUMN PINNING
      columnPinning: {
        enabled: true,
        allowUserPinning: false
      },

      // STYLING
      styling: {
        density: 'comfortable',
        maxHeight: '70vh',
        headerHeight: 56,
        showBorders: true,
        stripedRows: false,
        rowHover: true,
        cellPaddingY: '0px'
      },

      // LOADING
      loading: {
        showOverlay: true,
        loadingText: 'Cargando materiales...'
      },

      // EMPTY STATE
      emptyState: {
        message: 'No hay materiales de trabajo en curso para mostrar.',
        showIcon: true,
        icon: 'inbox'
      },

      // STATE PERSISTENCE
      statePersistence: {
        enabled: true,
        storageKey: 'work-progress-material-summary-grid',
        storageType: 'localStorage',
        features: {
          columnVisibility: true,
          columnPinning: true,
          sorting: false,
          filters: false,
          pagination: false
        }
      },

      // RESPONSIVE
      responsive: {
        enabled: true,
        breakpoints: {
          mobile: 640,
          tablet: 1024
        },
        mobileMode: 'compact',
        autoHideColumns: true
      }
    };
  }

  private initializeBatchesGridConfig(): void {
    this.batchesGridConfig = {
      pagination: { enabled: false },
      sorting: { enabled: false },
      search: { enabled: false },
      columnFilters: { enabled: false },
      selection: { enabled: false },
      settingsPanel: { enabled: false },
      styling: {
        density: 'compact',
        maxHeight: '400px',
        showBorders: false
      }
    };
  }

  /**
   * Abre el modal de exportación para exportar materiales
   */
  onExportButtonPressed() {
    if (!this.workProgressMaterialExport.canStartTask()) return;

    const materials = this.materialData?.materials || [];
    if (!materials || materials.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: WORK_PROGRESS_MATERIAL_TITLE },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        // For now, always export all data since smart-grid handles pagination internally
        // In future, we can get visible/filtered data from smart-grid
        this.workProgressMaterialExport.export(
          options.format,
          options.range,
          0, // tripId not needed when we pass materials
          materials
        );
      }
    });
  }

  /**
   * Exporta un material específico usando el modal de exportación
   */
  exportSingleMaterial(material: WorkProgressMaterialItem) {
    if (!this.workProgressMaterialExport.canStartTask()) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: `Exportar ${material.materialName}` },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.workProgressMaterialExport.exportSingleMaterial(
          options.format,
          material
        );
      }
    });
  }

  /**
   * Exporta solo los lotes de un material específico
   */
  exportMaterialBatches(material: WorkProgressMaterialItem) {
    if (!this.workProgressMaterialExport.canStartTask()) return;

    if (!material.hasBatches) {
      return;
    }

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: `Exportar Lotes - ${material.materialName}` },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.workProgressMaterialExport.exportMaterialBatches(
          options.format,
          material
        );
      }
    });
  }

  openBatchesDetail(
    material: WorkProgressMaterialItem
  ) {
    // Transform the material data using the service
    const batchesData = this.simpleBatchesDataService.transformWorkProgressMaterialToBatchesData(
      material,
      {
        showUMP: this.currentUMPActive,
        showLabels: true
      }
    );

    const dialogRef = this.dialog.open<SimpleBatchesViewerComponent, SimpleBatchesViewerComponent['data']>(SimpleBatchesViewerComponent, {
      width: "90%",
      data: batchesData,
    });

    dialogRef.afterClosed().subscribe((result: SimpleBatchesDialogResponse | undefined) => {
      if (result?.action === 'close') {
        // Handle dialog close if needed
      }
    });
  }

  getTotalBaseQuantity(): number {
    const materials = this.materialData?.materials || [];
    if (materials.length === 0) {
      return 0;
    }
    return materials.reduce((total, material) => {
      const baseQuantity = material?.baseQuantity || 0;
      return total + baseQuantity;
    }, 0);
  }

  getTotalParallelQuantity(): number {
    if (!(this.currentUMPActive)) {
      return 0;
    }
    const materials = this.materialData?.materials || [];
    if (materials.length === 0) {
      return 0;
    }
    return materials.reduce((total, material) => {
      const parallelQuantity = material?.parallelQuantity || 0;
      return total + parallelQuantity;
    }, 0);
  }

  getCommonBaseUnit(): string {
    const materials = this.materialData?.materials || [];
    if (materials.length === 0) {
      return '';
    }
    const firstUnit = materials[0]?.baseUnitOfMeasure?.name;
    if (!firstUnit) return '';
    const allHaveSameUnit = materials.every(material =>
      material?.baseUnitOfMeasure?.name === firstUnit
    );
    return allHaveSameUnit ? firstUnit : 'Mixtas';
  }

  getCommonParallelUnit(): string {
    if (!(this.currentUMPActive)) {
      return '';
    }
    const materials = this.materialData?.materials || [];
    if (materials.length === 0) {
      return '';
    }
    const firstUnit = materials[0]?.parallelUnitOfMeasure?.name;
    if (!firstUnit) return '';
    const allHaveSameUnit = materials.every(material =>
      material?.parallelUnitOfMeasure?.name === firstUnit
    );
    return allHaveSameUnit ? firstUnit : 'Mixtas';
  }
}
